import dayjs from 'dayjs';

/** @typedef {{ id: string, dates: string[] }} HolidayEvent */

/** Per-date labels for the shared Christmas / New Year block (AU + UK). */
export const CHRISTMAS_NYE_PERIOD_DATE_LABELS = {
  '2026-12-21': 'Holiday period',
  '2026-12-22': 'Holiday period',
  '2026-12-23': 'Holiday period',
  '2026-12-24': 'Christmas Eve',
  '2026-12-25': 'Christmas Day',
  '2026-12-26': 'Boxing Day',
  '2026-12-27': 'Christmas holiday',
  '2026-12-28': 'Christmas holiday',
  '2026-12-29': 'Christmas holiday',
  '2026-12-30': 'Christmas holiday',
  '2026-12-31': "New Year's Eve",
  '2027-01-01': "New Year's Day",
  '2027-01-02': 'Christmas holiday',
  '2027-01-03': 'Christmas holiday',
};

/** Short title for modal list / analytics (one row per holiday id). */
const HOLIDAY_SUMMARY_TITLE = {
  CHRISTMAS_NYE_PERIOD_2026: 'Christmas & New Year period',
  ANZAC_DAY_2026: 'Anzac Day',
  QLD_LABOUR_DAY_2026: 'Labour Day',
  UK_EARLY_MAY_BANK_HOLIDAY_2026: 'Early May bank holiday',
  UK_SPRING_BANK_HOLIDAY_2026: 'Spring bank holiday',
  UK_SUMMER_BANK_HOLIDAY_2026: 'Summer bank holiday',
  NSW_BANK_HOLIDAY_2026: 'Bank holiday',
  NSW_ACT_SA_LABOUR_DAY_2026: 'Labour Day',
  QLD_KINGS_BIRTHDAY_2026: "King's Birthday",
  WA_KINGS_BIRTHDAY_2026: "King's Birthday",
  KINGS_BIRTHDAY_2026: "King's Birthday",
  EKKA_ROYAL_QUEENSLAND_SHOW_2026: 'Ekka (Royal Queensland Show)',
};

/**
 * @param {string} holidayId
 * @returns {string}
 */
export function formatHolidayIdToLabel(holidayId) {
  const withoutYear = holidayId.replace(/_\d{4}$/, '');
  const words = withoutYear.split('_').filter(Boolean);
  const lower = words.map((w) => w.toLowerCase());
  return lower
    .map((w, i) => {
      if (i === 0 && w === 'anzac') return 'Anzac';
      if (w === 'uk') return 'UK';
      if (w === 'nz') return 'NZ';
      if (w === 'wa') return 'WA';
      if (w === 'vic') return 'VIC';
      if (w === 'nsw') return 'NSW';
      if (w === 'act') return 'ACT';
      if (w === 'qld') return 'QLD';
      if (w === 'tas') return 'TAS';
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(' ');
}

/**
 * Label for a single calendar cell (publicHolidays map).
 * @param {string} holidayId
 * @param {string} isoDate YYYY-MM-DD
 * @returns {string}
 */
export function getHolidayDateCellLabel(holidayId, isoDate) {
  if (holidayId === 'CHRISTMAS_NYE_PERIOD_2026') {
    return CHRISTMAS_NYE_PERIOD_DATE_LABELS[isoDate] ?? 'Holiday period';
  }
  if (HOLIDAY_SUMMARY_TITLE[holidayId]) {
    return HOLIDAY_SUMMARY_TITLE[holidayId];
  }
  return formatHolidayIdToLabel(holidayId);
}

/**
 * Title shown in the modal list for one holiday entry.
 * @param {string} holidayId
 * @returns {string}
 */
export function getHolidaySummaryTitle(holidayId) {
  return HOLIDAY_SUMMARY_TITLE[holidayId] ?? formatHolidayIdToLabel(holidayId);
}

/**
 * All holiday events that apply to a venue region.
 * @param {{ holidays?: { name: string, holidayGroups?: { regions: string[], dates: string[] }[] }[] }} holidayData
 * @param {string|null|undefined} region
 * @returns {HolidayEvent[]}
 */
export function getHolidayEventsForRegion(holidayData, region) {
  if (!region || !holidayData?.holidays?.length) {
    return [];
  }

  return holidayData.holidays
    .map((h) => {
      const dates = (h.holidayGroups ?? []).flatMap((g) =>
        g.regions?.includes(region) ? (g.dates ?? []) : [],
      );
      if (!dates.length) {
        return null;
      }
      const uniqueSorted = [...new Set(dates)].sort();
      return { id: h.name, dates: uniqueSorted };
    })
    .filter(Boolean);
}

/**
 * Flat map for holiday hours table + API bounds (all dates in scope).
 * @param {HolidayEvent[]} events
 * @returns {Record<string, string>}
 */
export function buildPublicHolidaysMap(events) {
  /** @type {Record<string, string>} */
  const map = {};
  events.forEach((ev) => {
    ev.dates.forEach((isoDate) => {
      map[isoDate] = getHolidayDateCellLabel(ev.id, isoDate);
    });
  });
  return map;
}

/**
 * Holiday dates for the active modal campaign only (excludes past holidays whose
 * lead window overlaps a later one, e.g. UK Early May when Spring Bank is upcoming).
 *
 * @param {Record<string, string>} fullMap
 * @param {{ id: string, dates: string[] }[]|undefined} summaries
 * @param {import('dayjs').Dayjs|string|Date} [todayInput]
 * @returns {Record<string, string>}
 */
export function buildActivePublicHolidaysMap(fullMap, summaries, todayInput = dayjs()) {
  if (!summaries?.length) {
    return {};
  }

  const today = dayjs(todayInput).startOf('day');
  /** @type {Record<string, string>} */
  const scoped = {};

  summaries.forEach((summary) => {
    const sorted = [...(summary.dates ?? [])].sort();
    if (!sorted.length) {
      return;
    }
    const eventEnd = dayjs(sorted[sorted.length - 1]).startOf('day');
    if (eventEnd.isBefore(today, 'day')) {
      return;
    }
    sorted.forEach((isoDate) => {
      if (fullMap[isoDate]) {
        scoped[isoDate] = fullMap[isoDate];
      }
    });
  });

  return scoped;
}

/**
 * Editable date range for holiday hours UI / deep links (actual holiday dates only).
 *
 * @param {{ dates: string[] }[]|undefined} summaries
 * @returns {{ startDate: string, endDate: string }|null}
 */
export function getEditableHolidayDateBounds(summaries) {
  if (!summaries?.length) {
    return null;
  }

  const dates = [...new Set(summaries.flatMap((s) => s.dates ?? []))].sort();
  if (!dates.length) {
    return null;
  }

  return { startDate: dates[0], endDate: dates[dates.length - 1] };
}

/**
 * Active modal campaign: venues should see the prompt while today is within
 * [min(eventStart) - leadDays, max(eventEnd)] across all holidays that are
 * currently in their own per-holiday window.
 *
 * @param {HolidayEvent[]} events
 * @param {import('dayjs').Dayjs|string|Date} todayInput
 * @param {number} [leadDays=21]
 * @returns {{ hdsModalStartDate: string, hdsModalEndDate: string, summaries: { id: string, displayName: string, dates: string[] }[] }|null}
 */
export function computeModalCampaign(events, todayInput, leadDays = 21) {
  const today = dayjs(todayInput).startOf('day');

  /** @type {{ id: string, dates: string[], eventStart: string, eventEnd: string, displayName: string }[]} */
  const active = events
    .filter((ev) => ev.dates?.length)
    .map((ev) => {
      const sorted = [...ev.dates].sort();
      const eventStart = dayjs(sorted[0]).startOf('day');
      const eventEnd = dayjs(sorted[sorted.length - 1]).startOf('day');
      const windowStart = eventStart.subtract(leadDays, 'day');

      const inWindow =
        (today.isAfter(windowStart, 'day') || today.isSame(windowStart, 'day')) &&
        (today.isBefore(eventEnd, 'day') || today.isSame(eventEnd, 'day'));

      if (!inWindow) {
        return null;
      }

      return {
        id: ev.id,
        dates: sorted,
        eventStart: sorted[0],
        eventEnd: sorted[sorted.length - 1],
        displayName: getHolidaySummaryTitle(ev.id),
      };
    })
    .filter(Boolean);

  if (!active.length) {
    return null;
  }

  const minModalStart = active.reduce((min, a) => {
    const ws = dayjs(a.eventStart).subtract(leadDays, 'day');
    if (min === null || ws.isBefore(min, 'day')) {
      return ws;
    }
    return min;
  }, /** @type {import('dayjs').Dayjs | null} */ (null));

  const maxEventEnd = active.reduce((max, a) => {
    const ee = dayjs(a.eventEnd);
    if (max === null || ee.isAfter(max, 'day')) {
      return ee;
    }
    return max;
  }, /** @type {import('dayjs').Dayjs | null} */ (null));

  return {
    hdsModalStartDate: minModalStart.format('YYYY-MM-DD'),
    hdsModalEndDate: maxEventEnd.format('YYYY-MM-DD'),
    summaries: active.map(({ id, displayName, dates }) => ({ id, displayName, dates })),
  };
}
